import React, { FC } from 'react'
import { Controller, ControllerProps } from 'react-hook-form'
import { Dropzone, IDropzoneProps, IFileObject } from 'transfix-ui/components/Dropzone'
import { IFileCreate } from 'types'
import uuid from 'uuid'

/**
 * Maps the values from MUI-Dropzone into the format that Fleet Planner expects for file upload.
 */
export const mapDropzoneFiles = (files: IFileObject[] = []): IFileCreate[] => {
  return files.map(f => {
    const { data, file } = f || {}
    const { name } = file || {}
    return { file: data, fileFileName: name, uuid: uuid() }
  })
}

type IBaseControllerProps = ControllerProps<React.ComponentType<IDropzoneProps>>

export interface IControlledDropzoneProps
  extends Pick<IBaseControllerProps, 'control' | 'name' | 'defaultValue'> {
  DropzoneProps?: Partial<IDropzoneProps>
}

/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledDropzone
 *   name='file'
 *   control={control}
 *   DropzoneProps={{
 *     dropzoneText: 'Drag and drop files here or click to upload',
 *   }}
 * />
 * ```
 */
const ControlledDropzone: FC<IControlledDropzoneProps> = ({
  control,
  defaultValue,
  name,
  DropzoneProps = {},
}) => {
  const { dropZoneText = 'Upload', ...rest } = DropzoneProps || {}
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ onChange }) => {
        const fileChangeHandler = (docs: IFileObject[]) => {
          if (docs) {
            const pkgFiles = mapDropzoneFiles(docs)
            if (pkgFiles) onChange(pkgFiles)
          }
        }

        return <Dropzone dropZoneText={dropZoneText} onFileChange={fileChangeHandler} {...rest} />
      }}
    />
  )
}

export default ControlledDropzone
